import { ArrowRight, Pencil } from "lucide-react";

import { DataStorageNotice } from "@/components/data-storage-notice";
import { Eyebrow } from "@/components/eyebrow";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import type { AnswerSuggestion, DefinitionLayer } from "@/lib/api";
import { cn } from "@/lib/utils";

interface ReviewStepProps {
  layers: DefinitionLayer[];
  answers: Record<string, number>;
  suggestions: Record<string, AnswerSuggestion>;
  totalQuestions: number;
  submitting: boolean;
  /** Session state still resolving: hold the submit disabled so we neither
   *  fire an anonymous POST nor gate before we know who the user is. */
  authLoading: boolean;
  error: string | null;
  onEdit: (questionId: string) => void;
  onBack: () => void;
  onSubmit: () => void;
}

export function ReviewStep({
  layers,
  answers,
  suggestions,
  totalQuestions,
  submitting,
  authLoading,
  error,
  onEdit,
  onBack,
  onSubmit,
}: ReviewStepProps) {
  const answeredCount = Object.keys(answers).length;

  return (
    <div>
      <Eyebrow>Final step · Review</Eyebrow>
      <h1 className="text-primary mt-3 text-4xl font-normal tracking-tight sm:text-5xl">
        Review your answers.
      </h1>
      <p className="text-muted-foreground mt-4 mb-6 text-lg">
        {answeredCount} of {totalQuestions} answered. Edit anything before we generate the report.
      </p>

      {error && (
        <div className="border-destructive bg-destructive/10 text-destructive mb-6 rounded-md border px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {layers.map((layer) => (
        <Card key={layer.layer} className="border-accent mb-5">
          <CardContent className="pt-6">
            <Eyebrow>{layer.title}</Eyebrow>
            {layer.dimensions.map((dim) =>
              dim.questions.map((q) => {
                const value = answers[q.question_id];
                const chosen =
                  value === undefined
                    ? null
                    : (q.options.find((o) => o.value === value)?.label ?? String(value));
                const suggested = suggestions[q.question_id] !== undefined;
                return (
                  <div
                    key={q.question_id}
                    className="border-border/60 flex justify-between gap-4 border-b py-2.5 last:border-b-0"
                  >
                    <div className="flex-1">
                      <div className="text-sm">{q.text}</div>
                      <div
                        className={cn(
                          "mt-1 font-bold",
                          chosen ? "text-primary" : "text-muted-foreground",
                        )}
                      >
                        {chosen ?? "Skipped"}
                        {suggested && chosen && (
                          <span className="text-accent ml-2 font-mono text-[11px] font-normal tracking-wide uppercase">
                            suggested
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="text-accent inline-flex items-center gap-1 self-center text-xs font-medium hover:underline"
                      onClick={() => onEdit(q.question_id)}
                    >
                      <Pencil className="h-3 w-3" /> Edit
                    </button>
                  </div>
                );
              }),
            )}
          </CardContent>
        </Card>
      ))}

      <div className="mt-8 flex items-center justify-between">
        <Button variant="ghost" onClick={onBack}>
          ← Back to questions
        </Button>
        <Button onClick={onSubmit} disabled={submitting || authLoading || answeredCount === 0}>
          {submitting ? "Generating report…" : "Generate report"} <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
      <p className="text-muted mt-4 text-right text-xs">
        <DataStorageNotice />
      </p>
    </div>
  );
}
